import React, { useState, useContext, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";

import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import AddIcon from "@mui/icons-material/Add";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

// My Files
import { DatabaseContext, UserContext } from "../../../context";
import ToolbarWithLogo from "../../ToolbarWithLogo";
import UserAvatar from "../UserAvatar";
import firebase from "../../../firebase";
import NoPermissionsMessage from "../../NoPermissionsMessage";
import {
    primaryFocusRing,
    primarySurface,
    primaryTint,
    primaryTintBorder,
} from "../../../surfaceTints";
import { jsonFromArray, makeid } from "../../../utility";
import NewHubDialog from "./NewHubDialog";

export default function HubListPage() {
    const history = useHistory();
    const location = useLocation();

    const [current, setCurrent] = useState({ name: "Hubs", key: "Root" }); // The currently selected parent hub

    const { folders, foldersLoaded, users } = useContext(DatabaseContext);

    const [userCanAccess, setUserCanAccess] = useState();
    const [userCanAdd, setUserCanAdd] = useState();

    const [breadcrumbs, setBreadcrumbs] = useState([
        { name: "Hubs", key: "Root" },
    ]); // Used to keep track of the parent groups as user navigates down the tree, then use this to implement the breadcrumb

    const { currentUserData } = useContext(UserContext);

    const [visibleHubs, setVisibleHubs] = useState([]); // Just the hubs that are visible at the moment

    const [noHubsAtThisLevel, setNoHubsAtThisLevel] = useState(false);

    const [newHubDialogOpen, setNewHubDialogOpen] = useState(false);

    // When the user selects a hub, this is used to store the trail back up the tree to root. So the breadcrumb can be updated.
    let parentNodeTrail = [];

    useEffect(() => {
        const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
            // detaching the listener
            if (!user) {
                // If no user is logged in, redirect to sign in page
                // history.replace("/signin");
                history.replace("/signin?url=" + location.pathname);
            }
        });

        if (currentUserData) {
            if (
                currentUserData.role === "Player" ||
                currentUserData.role === "FAW Player" ||
                currentUserData.role === "Club Player" ||
                currentUserData.role === "Coach Ed Student" ||
                currentUserData.role === "FAW Coach Ed Student" ||
                currentUserData.role === "Club Coach Ed Student" ||
                currentUserData.role === "FAW Parent" ||
                currentUserData.role === "Club Parent" ||
                currentUserData.role === "Referee"
            ) {
                setUserCanAccess(false);
            } else {
                setUserCanAccess(true);
            }
        }
        let key = location.pathname.replace("/hubs/", "");

        let currentHub = folders.find((h) => h.key === key);
        if (currentHub) {
            setCurrent(currentHub);
            if (currentUserData.role === "System Digital Manager") {
                setUserCanAdd(true);
            } else if (
                currentHub.managers &&
                currentHub.managers.includes(currentUserData.key)
            ) {
                setUserCanAdd(true);
            } else {
                setUserCanAdd(false);
            }

            // Get the route back to the root node for the breadcrumb.
            // parentNodeTrail is a deliberate per-render scratch buffer: this
            // effect fills it and the recursive getParentNodes helper below
            // pushes onto it, so it cannot move inside the effect.
            if (currentHub.key === "root") {
                // Don't think it ever goes in here
                // eslint-disable-next-line react-hooks/exhaustive-deps
                parentNodeTrail = [];
            } else {
                parentNodeTrail = [
                    {
                        name: currentHub.name,
                        key: currentHub.key,
                        parent: currentHub.parent,
                    },
                ];
            }
            //

            let potentiallyVisibleHubs = folders.filter(
                (f) => f.hub && f.parent === currentHub.key,
            );

            potentiallyVisibleHubs = potentiallyVisibleHubs.sort((a, b) =>
                a.name > b.name ? 1 : -1,
            );

            if (potentiallyVisibleHubs.length === 0) {
                setNoHubsAtThisLevel(true);
            } else {
                setNoHubsAtThisLevel(false);
            }

            if (
                currentUserData &&
                currentUserData.role === "System Digital Manager"
            ) {
                setVisibleHubs(potentiallyVisibleHubs);
            } else {
                let visibleHubsArray = [];
                // Out of the hubs that could be shown at this point, check if the currentUser is a manager of any hub down the hierarchy, if so, should show that hub.
                potentiallyVisibleHubs.forEach((hub) => {
                    folders
                        .filter(
                            (f) =>
                                f.hub &&
                                (f.ancestors.includes(hub.key) ||
                                    f.key === hub.key),
                        )
                        .forEach((descendantHub) => {
                            if (
                                descendantHub.managers.includes(
                                    currentUserData.key,
                                )
                            ) {
                                if (!visibleHubsArray.includes(hub)) {
                                    visibleHubsArray.push(hub);
                                }
                            }
                        });
                });

                setVisibleHubs(visibleHubsArray);
            }

            getParents(currentHub.key);

            parentNodeTrail.push({
                name: "Hubs",
                key: "Root",
            });

            parentNodeTrail.reverse();

            setBreadcrumbs(parentNodeTrail);
        } else {
            let potentiallyVisibleHubs = folders.filter(
                (f) => f.hub && f.parent === "Root",
            );

            potentiallyVisibleHubs = potentiallyVisibleHubs.sort((a, b) =>
                a.name > b.name ? 1 : -1,
            );

            if (potentiallyVisibleHubs.length === 0) {
                setNoHubsAtThisLevel(true);
            } else {
                setNoHubsAtThisLevel(false);
            }

            if (
                currentUserData &&
                currentUserData.role === "System Digital Manager"
            ) {
                setUserCanAdd(true);

                setVisibleHubs(potentiallyVisibleHubs);
            } else {
                setUserCanAdd(false);

                let visibleHubsArray = [];
                // Out of the hubs that could be shown at this point, check if the currentUser is a manager of any hub down the hierarchy, if so, should show that hub.
                potentiallyVisibleHubs.forEach((hub) => {
                    folders
                        .filter(
                            (f) =>
                                f.hub &&
                                (f.ancestors.includes(hub.key) ||
                                    f.key === hub.key),
                        )
                        .forEach((descendantHub) => {
                            if (
                                descendantHub.managers.includes(
                                    currentUserData.key,
                                )
                            ) {
                                if (!visibleHubsArray.includes(hub)) {
                                    visibleHubsArray.push(hub);
                                }
                            }
                        });
                });

                setVisibleHubs(visibleHubsArray);
            }
        }

        return () => unsubscribe();
    }, [location.pathname, folders, history, currentUserData]);

    // Recursively gets the parentNodes for a folderNode. Puts them in parentNodeTrail array
    function getParents(key) {
        let folder = folders.find((f) => f.key === key);

        if (folder) {
            let parentFolder = folders.find((f) => f.key === folder.parent);

            if (parentFolder) {
                parentNodeTrail.push({
                    name: parentFolder.name,
                    key: parentFolder.key,
                    parent: parentFolder.parent,
                });
                getParents(parentFolder.key);
            }
        }
    }

    function handleAddClick() {
        setNewHubDialogOpen(true);
    }

    const handleHubClick = (hub) => () => {
        setCurrent(hub);

        setBreadcrumbs((prevValue) => {
            return [...prevValue, { name: hub.name, key: hub.key }];
        });

        if (hub.name === "Root") {
            history.push("/hubs");
        } else {
            history.push(`/hubs/${hub.key}`);
        }
    };

    const handleManageHubClick = (hub) => () => {
        history.push("/managehub?key=" + hub.key);
    };

    const handleBreadcrumbClick = (breadcrumb) => () => {
        // Remove breadcrumbs after (and including) the one that has been pressed
        let index = 0;

        for (index = 0; index < breadcrumbs.length; index++) {
            let hub = breadcrumbs[index];

            if (hub.key === breadcrumb.key) {
                break;
            }
        }

        var newBreadCrumbs = [...breadcrumbs];

        newBreadCrumbs.length = index + 1;

        setBreadcrumbs(newBreadCrumbs);

        // Set the current parent to the breadcrumb that has been selected
        setCurrent(breadcrumb);

        if (breadcrumb.name === "Root") {
            history.replace("/hubs");
        } else {
            history.replace(`/hubs/${breadcrumb.key}`);
        }
    };

    function handleCloseNewHubDialog(hubName, okPressed) {
        if (hubName !== "" && okPressed) {
            // Create Hub in firebase
            var ref = firebase.database().ref("folders");

            // Have to convert arrays to Objects for storage in firebase
            var ancestorsObject = [];
            var usersObject = [];
            var managersObject = [];

            if (current.ancestors && current.key !== "Root") {
                ancestorsObject = jsonFromArray(current.ancestors);
                ancestorsObject[current.key] = true;

                if (current.users) {
                    usersObject = jsonFromArray(current.users);
                }

                if (current.managers) {
                }
                managersObject = jsonFromArray(current.managers);
            }

            let newHubKey = makeid(19);

            ref.child(newHubKey).set({
                hub: true,
                name: hubName,
                parent: current.key,
                ancestors: ancestorsObject,
                users: usersObject,
                managers: managersObject,
                imageURL: "",
            });

            // Hub has been added

            // Create All Users group for the hub
            let groupsRef = firebase.database().ref("groups");

            groupsRef.push({
                name: "All Users",
                hub: newHubKey,
                users: usersObject,
            });

            // When a user is added to a hub, need to set their user.admins array to contain all the managers of this hub
            if (current.users) {
                current.users.forEach((userKey) => {
                    let user = users.find((u) => u.key === userKey);

                    if (user) {
                        let userRef = firebase
                            .database()
                            .ref("users")
                            .child(user.key);
                        let userAdminsObject = {};

                        // Preserve the existing contents of user.admins
                        user.admins.forEach((adminKey) => {
                            userAdminsObject[adminKey] = true;
                        });

                        // Then add the managers from the current hub
                        current.managers.forEach((managerKey) => {
                            userAdminsObject[managerKey] = true;
                        });

                        userRef.update({ admins: userAdminsObject });
                    }
                });
            }
        }

        setNewHubDialogOpen(false);
    }
    return (
        <React.Fragment>
            {userCanAccess && (
                <Box
                    component="main"
                    sx={{
                        minHeight: "calc(100vh - 64px)",
                        backgroundColor: "background.default",
                        pt: { xs: 2, sm: 3, md: 4 },
                        pb: { xs: 2, sm: 4 },
                    }}
                >
                    <Container maxWidth="md" sx={{ px: { xs: 1.5, sm: 3 } }}>
                        <Card
                            variant="outlined"
                            sx={{
                                overflow: "hidden",
                                borderRadius: 3,
                                borderColor: "divider",
                                backgroundColor: "background.paper",
                                boxShadow: (theme) =>
                                    theme.palette.mode === "dark"
                                        ? "0 8px 28px rgba(0, 0, 0, 0.3)"
                                        : "0 8px 28px rgba(20, 35, 25, 0.075)",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: {
                                        xs: "flex-start",
                                        sm: "center",
                                    },
                                    justifyContent: "space-between",
                                    flexDirection: {
                                        xs: "column",
                                        sm: "row",
                                    },
                                    gap: 2,
                                    p: { xs: 2.5, sm: 3.5 },
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.75,
                                        minWidth: 0,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: 2.5,
                                            display: "grid",
                                            placeItems: "center",
                                            flexShrink: 0,
                                            color: "primary.main",
                                            backgroundColor: primaryTint,
                                            border: "1px solid",
                                            borderColor: primaryTintBorder,
                                        }}
                                    >
                                        <AccountTreeOutlinedIcon />
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                            variant="h4"
                                            component="h1"
                                            sx={{
                                                fontSize: { xs: 27, sm: 32 },
                                                lineHeight: 1.15,
                                            }}
                                        >
                                            Hubs
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                mt: 0.5,
                                                color: "text.secondary",
                                            }}
                                        >
                                            Browse and manage the hub hierarchy.
                                        </Typography>
                                    </Box>
                                </Box>

                                {userCanAdd && (
                                    <Button
                                        variant="contained"
                                        disableElevation
                                        startIcon={<AddIcon />}
                                        onClick={handleAddClick}
                                        sx={{
                                            flexShrink: 0,
                                            alignSelf: {
                                                xs: "stretch",
                                                sm: "center",
                                            },
                                            px: 2.25,
                                            py: 1.1,
                                            fontWeight: 700,
                                        }}
                                    >
                                        Add hub
                                    </Button>
                                )}
                            </Box>

                            <Divider />

                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 1.25,
                                    mx: { xs: 2, sm: 2.5 },
                                    my: 2,
                                    p: { xs: 1.5, sm: 2 },
                                    borderRadius: 2.5,
                                    color: "text.secondary",
                                    border: "1px solid",
                                    borderColor: primaryTintBorder,
                                    backgroundColor: primarySurface,
                                }}
                            >
                                <InfoOutlinedIcon
                                    sx={{
                                        mt: 0.1,
                                        flexShrink: 0,
                                        color: "primary.main",
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    sx={{ lineHeight: 1.55 }}
                                >
                                    Open a hub to browse its children. Use the
                                    settings button to manage the hub.
                                </Typography>
                            </Box>

                            <Breadcrumbs
                                separator="›"
                                aria-label="Hub hierarchy"
                                sx={{
                                    px: { xs: 2, sm: 2.5 },
                                    py: 1.75,
                                    borderTop: "1px solid",
                                    borderBottom: "1px solid",
                                    borderColor: "divider",
                                    backgroundColor: "background.subtle",
                                }}
                            >
                                {breadcrumbs.map((breadcrumb) => (
                                    <Link
                                        component="button"
                                        type="button"
                                        onClick={handleBreadcrumbClick(
                                            breadcrumb,
                                        )}
                                        key={breadcrumb.key}
                                        underline="hover"
                                        sx={{
                                            border: 0,
                                            p: 0,
                                            font: "inherit",
                                            cursor: "pointer",
                                            background: "none",
                                        }}
                                    >
                                        {breadcrumb.name}
                                    </Link>
                                ))}
                            </Breadcrumbs>

                            {!foldersLoaded ? (
                                <Box
                                    aria-label="Loading hubs"
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 1,
                                        p: { xs: 1.5, sm: 2 },
                                    }}
                                >
                                    {[0, 1, 2].map((item) => (
                                        <Box
                                            key={item}
                                            sx={{
                                                minHeight: 82,
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1.5,
                                                px: 1.5,
                                                py: 1.25,
                                                borderRadius: 2.5,
                                                border: "1px solid",
                                                borderColor: "divider",
                                                backgroundColor:
                                                    "background.paper",
                                            }}
                                        >
                                            <Skeleton
                                                variant="rounded"
                                                width={56}
                                                height={56}
                                                sx={{ flexShrink: 0 }}
                                            />
                                            <Box sx={{ flex: 1 }}>
                                                <Skeleton
                                                    variant="text"
                                                    width="42%"
                                                    height={24}
                                                />
                                                <Skeleton
                                                    variant="text"
                                                    width="66%"
                                                    height={19}
                                                />
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            ) : noHubsAtThisLevel ||
                              visibleHubs.length === 0 ? (
                                <Box
                                    role="status"
                                    sx={{
                                        minHeight: 270,
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        px: 3,
                                        textAlign: "center",
                                        color: "text.secondary",
                                    }}
                                >
                                    <AccountTreeOutlinedIcon
                                        sx={{
                                            fontSize: 44,
                                            mb: 1,
                                            opacity: 0.35,
                                        }}
                                    />
                                    <Typography
                                        variant="h6"
                                        component="h2"
                                        sx={{
                                            color: "text.primary",
                                            fontWeight: 700,
                                        }}
                                    >
                                        No hubs at this level
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ mt: 0.5 }}
                                    >
                                        {userCanAdd
                                            ? "Add a hub here or use the breadcrumb trail to go back."
                                            : "Use the breadcrumb trail to return to a parent hub."}
                                    </Typography>
                                    {userCanAdd && (
                                        <Button
                                            variant="outlined"
                                            startIcon={<AddIcon />}
                                            onClick={handleAddClick}
                                            sx={{ mt: 2.5 }}
                                        >
                                            Add hub
                                        </Button>
                                    )}
                                </Box>
                            ) : (
                                <List
                                    disablePadding
                                    aria-label="Hubs"
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 1,
                                        p: { xs: 1.5, sm: 2 },
                                    }}
                                >
                                    {visibleHubs.map((hub) => {
                                        const canManage =
                                            currentUserData.role ===
                                                "System Digital Manager" ||
                                            hub.managers.includes(
                                                currentUserData.key,
                                            );

                                        return (
                                            <ListItem
                                                key={hub.key}
                                                disablePadding
                                                sx={{
                                                    overflow: "hidden",
                                                    borderRadius: 2.5,
                                                    border: "1px solid",
                                                    borderColor: "divider",
                                                    backgroundColor:
                                                        "background.paper",
                                                }}
                                            >
                                                <ListItemButton
                                                    onClick={handleHubClick(
                                                        hub,
                                                    )}
                                                    sx={{
                                                        minWidth: 0,
                                                        py: 1.25,
                                                        pl: 1.5,
                                                        pr: 1,
                                                        "&:hover": {
                                                            backgroundColor:
                                                                "action.hover",
                                                        },
                                                        "&:focus-visible": {
                                                            outline:
                                                                primaryFocusRing,
                                                            outlineOffset: -3,
                                                        },
                                                    }}
                                                >
                                                    <ListItemAvatar
                                                        sx={{ minWidth: 62 }}
                                                    >
                                                        <UserAvatar
                                                            userOrHub={hub}
                                                            path="hubs"
                                                            variant="rounded"
                                                            size="large"
                                                        />
                                                    </ListItemAvatar>
                                                    <ListItemText
                                                        primary={hub.name}
                                                        secondary={
                                                            canManage
                                                                ? "Open child hubs or manage this hub"
                                                                : "Open child hubs"
                                                        }
                                                        primaryTypographyProps={{
                                                            sx: {
                                                                fontWeight: 800,
                                                            },
                                                        }}
                                                    />
                                                    <ChevronRightIcon
                                                        aria-hidden="true"
                                                        sx={{
                                                            ml: 0.5,
                                                            color: "text.disabled",
                                                        }}
                                                    />
                                                </ListItemButton>
                                                {canManage && (
                                                    <Tooltip title="Manage hub">
                                                        <IconButton
                                                            aria-label={`Manage ${hub.name}`}
                                                            onClick={handleManageHubClick(
                                                                hub,
                                                            )}
                                                            sx={{
                                                                mr: 1,
                                                                color: "text.secondary",
                                                                "&:hover": {
                                                                    color: "primary.main",
                                                                    backgroundColor:
                                                                        "action.hover",
                                                                },
                                                            }}
                                                        >
                                                            <SettingsIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </ListItem>
                                        );
                                    })}
                                </List>
                            )}
                        </Card>
                    </Container>
                </Box>
            )}
            {userCanAccess === false && <NoPermissionsMessage />}
            {userCanAccess === undefined && (
                <Box
                    sx={{
                        minHeight: "calc(100vh - 64px)",
                        display: "grid",
                        placeItems: "center",
                        backgroundColor: "background.default",
                    }}
                >
                    <CircularProgress size={32} aria-label="Loading hubs" />
                </Box>
            )}
            <NewHubDialog
                open={newHubDialogOpen}
                onClose={handleCloseNewHubDialog}
            />
            <ToolbarWithLogo />
        </React.Fragment>
    );
}
